// incidentLinkingService.js
//
// Build 24: handles cross-app record creation/cancellation when an incident's
// trigger checkboxes are saved.
//
// Audit-friendly behavior (per Build 24 spec):
//   - Checking a trigger box on save: auto-create a linked record in the
//     target app with default values, OR reactivate an existing cancelled
//     record if one was previously cancelled from this same incident.
//   - Unchecking a trigger box on edit: mark the existing linked record as
//     cancelled (status='Cancelled', cancelledReason, cancelledAt), do NOT
//     delete. Preserves compliance/audit trail.
//
// Each target record carries `sourceIncidentId` for the link.

import {
  collection, query, where, getDocs, addDoc, updateDoc, doc,
  serverTimestamp
} from 'firebase/firestore';
import { db } from '../firebase';
import {
  nextInjuryNumber, nextMaintenanceNumber
} from './applicationCounterService';

const TRIGGER_CONFIG = {
  injury: {
    collection: 'injuries',
    counterFn: nextInjuryNumber,
    numberField: 'displayNumber',
    defaultStatus: 'Open'
  },
  maintenance: {
    collection: 'maintenanceRequests',
    counterFn: nextMaintenanceNumber,
    numberField: 'displayNumber',
    defaultStatus: 'Open'
  },
  sopViolation: {
    collection: 'sopEvents',
    counterFn: null, // SOP events are numbered by the parent SOP, not per-app
    numberField: null,
    defaultStatus: 'Open'
  }
};

/**
 * Find an existing linked record (active or cancelled) for this incident in
 * the target collection. Returns the doc snapshot or null.
 */
async function findLinkedRecord(triggerType, incidentId) {
  const cfg = TRIGGER_CONFIG[triggerType];
  if (!cfg) return null;
  const q = query(
    collection(db, cfg.collection),
    where('sourceIncidentId', '==', incidentId)
  );
  const snap = await getDocs(q);
  return snap.docs[0] || null;
}

/**
 * Create or reactivate a linked record in the target app for an incident
 * trigger. Called when a trigger checkbox transitions from false→true on save.
 */
async function activateTrigger(triggerType, incident, user) {
  const cfg = TRIGGER_CONFIG[triggerType];
  if (!cfg) return;

  // If an existing cancelled record already exists, reactivate it.
  const existing = await findLinkedRecord(triggerType, incident.id);
  if (existing) {
    await updateDoc(doc(db, cfg.collection, existing.id), {
      status: cfg.defaultStatus,
      cancelledAt: null,
      cancelledReason: null,
      reactivatedAt: serverTimestamp(),
      updatedAt: serverTimestamp()
    });
    return;
  }

  // Otherwise create a fresh record with defaults pulled from the incident.
  const base = {
    sourceIncidentId: incident.id,
    sourceIncidentTitle: incident.title || '',
    title: incident.title || '(auto-created from incident)',
    description: incident.description || '',
    location: incident.location || '',
    applicationName: incident.applicationName || '',
    status: cfg.defaultStatus,
    incidentDateTime: incident.incidentDateTime || null,
    createdAt: serverTimestamp(),
    updatedAt: serverTimestamp(),
    createdBy: user.uid,
    createdByEmail: user.email,
    autoCreatedFromIncident: true,
    updates: []
  };

  // Sequential number, if the target app supports it.
  if (cfg.counterFn && incident.applicationName) {
    try {
      const num = await cfg.counterFn(incident.applicationName);
      base[cfg.numberField] = num;
    } catch (err) {
      console.error(`Could not reserve ${triggerType} number:`, err);
    }
  }

  // SOP event records get extra fields specific to violations
  if (triggerType === 'sopViolation') {
    base.type = 'violation'; // distinguishes from deviations
    base.sopId = null;       // user will link to the specific SOP later
    base.sopVersionAtEvent = null;
  }

  await addDoc(collection(db, cfg.collection), base);
}

/**
 * Mark an existing linked record as cancelled when a trigger checkbox
 * transitions from true→false on save. Does NOT delete.
 */
async function cancelTrigger(triggerType, incidentId) {
  const cfg = TRIGGER_CONFIG[triggerType];
  if (!cfg) return;

  const existing = await findLinkedRecord(triggerType, incidentId);
  if (!existing) return; // nothing to cancel

  // Don't re-cancel an already-cancelled record
  if (existing.data().status === 'Cancelled') return;

  await updateDoc(doc(db, cfg.collection, existing.id), {
    status: 'Cancelled',
    cancelledReason: `Removed from incident ${incidentId}`,
    cancelledAt: serverTimestamp(),
    updatedAt: serverTimestamp()
  });
}

/**
 * Process all trigger checkboxes for an incident on save.
 *
 * @param incident          The just-saved incident object (must include id).
 * @param previousTriggers  The trigger state from before the save (for diffing).
 *                          Pass null on first creation.
 * @param user              The current Firebase user (for createdBy fields).
 */
export async function processIncidentTriggers(incident, previousTriggers, user) {
  const current = {
    injury: !!incident.triggersInjury,
    maintenance: !!incident.triggersMaintenance,
    sopViolation: !!incident.triggersSopViolation
  };
  const prev = previousTriggers || {
    injury: false,
    maintenance: false,
    sopViolation: false
  };

  const tasks = [];
  for (const key of Object.keys(current)) {
    if (current[key] && !prev[key]) {
      // false → true: activate
      tasks.push(activateTrigger(key, incident, user));
    } else if (!current[key] && prev[key]) {
      // true → false: cancel
      tasks.push(cancelTrigger(key, incident.id));
    }
    // Unchanged or both false: nothing to do
  }

  await Promise.all(tasks);
}

export { TRIGGER_CONFIG };
